import React, {
  useContext,
  useMemo,
  useState
} from 'react';
import { Alert } from 'react-bootstrap';
import { AdminInput, DragDropImageUpload } from '@project/components';
import formSchema from './schema';
import ThemeSelect from '../ThemeSelect/ThemeSelect';
import { RootContext } from '../../hoc/RootContext/RootContext';
import SpaceSelect from '../SpaceSelect/SpaceSelect';
import { useIntl } from 'react-intl';
import BuildForm from '../../components/BuildForm/BuildForm';
import UploadInteralFilesButton from '../../components/InternalFilesUploader/InternalFilesUploader';
import { renderField } from '../../utils/renderFields';
import axios from '../../utils/axios';
import { get } from '../../utils/utils';
import styles from './BasicInfoForm.module.css';
import spaceProfileDefaults, { setDefaults } from '../SpaceProfile/defaults';

const BasicInfoForm = ({
  layoutSchema,
  spaceProfile,
  setSpaceProfile,
  saveProfile,
  errors,
  setErrors,
  isReadOnly,
  fieldSettings,
  setIsFormDirty,
  openSection,
  setOpenSection,
  spaceProfilePhotos,
  setSpaceProfilePhotos,
  propertyDisplayName,
  spaceAssignment,
  generalSettings
}) => {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });

  const { currentUser } = useContext(RootContext);

  //Filtering for null because it's possible that {fileKey}_upload doesnt exist on the object
  const [featuredImage, setFeaturedImage] = useState([get(spaceProfile, 'basic_info.featured_upload')].filter(_ => _));
  const [additionalImages, setAdditionalImages] = useState([...get(spaceProfile, 'photos', [])]);
  const [featuredFloorplan, setFeaturedFloorplan] = useState([get(spaceProfile, 'basic_info.floorplan_upload')].filter(_ => _));
  const [additionalFloorplans, setAdditionalFloorplans] = useState([...get(spaceProfile, 'floorplans', [])]);
  const [lodPdf, setLodPdf] = useState([get(spaceProfile, 'basic_info.pdf_upload')].filter(_ => _));
  const [profilePdf, setProfilePdf] = useState([get(spaceProfile, 'basic_info.profile_pdf_upload')].filter(_ => _));
  const [autocad, setAutocad] = useState([get(spaceProfile, 'basic_info.autocad_upload')].filter(_ => _));
  const [loadingFile, setLoadingFile] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState('featuredImage');


  const setFiles = {
    'featuredImage': setFeaturedImage,
    'additionalImages': setAdditionalImages,
    'featuredFloorplan': setFeaturedFloorplan,
    'additionalFloorplans': setAdditionalFloorplans,
    'pdf': setLodPdf,
    'profile_pdf': setProfilePdf,
    'autocad': setAutocad
  }

  const uploadedFiles = {
    'featuredImage': featuredImage,
    'additionalImages': additionalImages,
    'featuredFloorplan': featuredFloorplan,
    'additionalFloorplans': additionalFloorplans,
    'pdf': lodPdf,
    'profile_pdf': profilePdf,
    'autocad': autocad
  }

  const uploadProps = {
    'featuredImage': { key: 'featured', numberFiles: 1, label: t('BasicInfoForm.upload.featured'), isSingleFileUpload: true },
    'additionalImages': { key: 'photos', numberFiles: 10, label: t('BasicInfoForm.upload.additionalImages'), hideButtons: true },
    'featuredFloorplan': { key: 'floorplan', numberFiles: 1, label: t('BasicInfoForm.upload.floorplans'), isSingleFileUpload: true },
    'additionalFloorplans': { key: 'floorplans', numberFiles: 10, label: t('BasicInfoForm.upload.additionalFloorplans'), hideButtons: true },
    'profile_pdf': { key: 'profile_pdf', numberFiles: 1, label: t('BasicInfoForm.upload.profilePdf'), fileTypes: ['application/pdf'], nonImageFileType: 'pdf', isSingleFileUpload: true },
    'pdf': { key: 'pdf', numberFiles: 1, label: t('BasicInfoForm.upload.lodPdf'), fileTypes: ['application/pdf'], nonImageFileType: 'pdf', isSingleFileUpload: true },
    'autocad': { key: 'autocad', numberFiles: 1, label: t('BasicInfoForm.upload.autocad'), fileTypes: ['.dwg'], nonImageFileType: 'dwg', isSingleFileUpload: true }
  }

  const createThumbnailKeys = ['featured'];

  const form = useMemo(() => spaceProfile.basic_info, [spaceProfile.basic_info]);
  const sqFootageError = useMemo(() => (
    form.actual_sq_ft &&
    form.client_provided_sq_ft &&
    Math.abs(1 - form.actual_sq_ft / form.client_provided_sq_ft) > .02
  ), [form.actual_sq_ft, form.client_provided_sq_ft]);

  const onChangeSpace = (option) => {
    if (!option || !option.value) return;
    updateField('asset_guid', { target: { value: option.assetGuid } }, spaceProfile);
    updateField('address2', { target: { value: option.value } }, spaceProfile);
    saveProfile(true);
  }

  const dynamicAddress = useMemo(() => {
    return spaceAssignment?.addressObserved || spaceAssignment?.address || spaceProfile?.address1 || null
  }, [spaceProfile, spaceAssignment])

  // updates the space profile object any time a field is changed
  const updateField = (field, { target: { type, value, checked } }, base, skipDirty, fullKey) => {
    let newErrors = errors && [...errors];
    newErrors = newErrors && newErrors.filter(error => error.key !== fullKey);
    setErrors(newErrors);
    if (!skipDirty) setIsFormDirty(true);
    const formObj = base || spaceProfile.basic_info;
    if (type === 'checkbox') {
      formObj[field] = checked;
    } else {
      formObj[field] = type === 'number' && value ? parseFloat(value) : value;
    }

    setSpaceProfile({ ...spaceProfile });
  }

  const insertValues = {
    'basic_info.size': get(spaceProfile, 'basic_info.size') && get(spaceProfile, 'basic_info.client_provided_sq_ft') ?
      <AdminInput
        label={t('assetSchema.office_area')}
        id={'office-area'}
        value={parseFloat((get(spaceProfile, 'basic_info.size') / get(spaceProfile, 'basic_info.client_provided_sq_ft') * 100).toFixed(1)) + '%'}
        readOnly={true}
        customstyle={'mt-3 mb-3'}
      /> : null
  }

  /* Internal file uploader settings */
  // The key required to POST and DELETE files in a bundle via the API.
  // It also serves as the key for this bundle in the `spaceProfile` object.
  const internalFilesBundleKey = 'internal_files';
  // All the files that are already stored as a bundle in the `spaceProfile` object.
  const internalFilesBundle = useMemo(() => {
    if (!spaceProfile.basic_info[internalFilesBundleKey]) {
      return [];
    }

    return spaceProfile.basic_info[internalFilesBundleKey];
  }, [spaceProfile]);

  const fileUploadErrorHandler = (key, existingFiles, deleteMode) => {
    setErrors([{ key, message: `${t('error')}: ${deleteMode ? t('BasicInfoForm.deleteFile.error') : t('BasicInfoForm.uploadFile.error')}` }]);
    //We delete files from the ui array before actual deletion is confirmed
    //If deletion fails we put the file back
    if (deleteMode) setFiles[key](existingFiles);
  }

  const fileUploadSuccessHandler = (key, fileKey, result, isSingleFileUpload) => {
    let spaceProfileResult = result.data.record;

    if (spaceProfileResult) {
      //After deletion `${fileKey}_upload` will be null so we need to filter
      setFiles[key]([
        ...(isSingleFileUpload ?
          [get(spaceProfileResult, `basic_info.${fileKey}_upload`)] :
          get(spaceProfileResult, `${fileKey}`, [])
        )].filter(_ => _));
      setDefaults(spaceProfileDefaults, spaceProfileResult);
      setSpaceProfile(spaceProfileResult);
    }
  }

  const fileUploadHasError = (result) => {
    return !get(result, 'data.success');
  }

  const saveFilesHandler = async (newFiles = [], key) => {
    const existingFiles = uploadedFiles[key];
    const isSingleFileUpload = uploadProps[key].isSingleFileUpload;
    const fileKey = uploadProps[key].key;

    try {
      setLoadingFile(true);
      setErrors([]);
      let result;

      //Single file upload covers uploads to basic_info. This includes creating a thumbnail if an image.
      //This is used for featured files (i.e. featured_upload, floorplan_upload, etc.)
      if (isSingleFileUpload) {
        const data = new FormData();
        data.append('file', newFiles[0]);
        data.append('company', spaceProfile.company);
        data.append('property', spaceProfile.property);
        if (createThumbnailKeys.includes(fileKey)) data.append('thumbnail', 'true');

        result = await axios.put(`/space-profiles/${spaceProfile.guid}/${fileKey}`, data, {
          company: spaceProfile.company
        });
      } else {
        //Uploads an array of files. No thumbnails created.
        const data = new FormData();
        if (newFiles) newFiles.forEach(file => data.append(fileKey, file));

        data.append('spaceProfile', JSON.stringify({ ...spaceProfile }));

        result = await axios.put(`/space-profiles/${spaceProfile.guid}`, data);
      }

      if (fileUploadHasError(result)) {
        fileUploadErrorHandler(key, existingFiles);
      } else {
        fileUploadSuccessHandler(key, fileKey, result, isSingleFileUpload)
      }
    } catch (e) {
      setErrors([{ key: 'generic', message: t('error.server') }]);
    } finally {
      setLoadingFile(false);
    }
  }

  const removeFileHandler = async (existingFiles = [], deleteIndex, key, replaceFileMode) => {
    const isSingleFileUpload = uploadProps[key].isSingleFileUpload;
    const fileKey = uploadProps[key].key;

    let updatedFiles = existingFiles.filter((files, i) => i !== deleteIndex);

    try {
      setLoadingFile(true);
      setErrors([]);

      setFiles[key](updatedFiles);

      const data = new FormData();
      //Single file upload deletes files from basic_info. Includes deleting accompanying thumbnail if present
      if (isSingleFileUpload) {
        data.append('spaceProfile', JSON.stringify({ ...spaceProfile, basic_info: { ...spaceProfile.basic_info, [`${fileKey}_upload`]: null, [`${fileKey}_thumbnail_upload`]: null } }));
      } else {
        data.append('spaceProfile', JSON.stringify({ ...spaceProfile, [fileKey]: updatedFiles }));
      }

      let result = await axios.put(`/space-profiles/${spaceProfile.guid}`, data);

      if (fileUploadHasError(result)) {
        fileUploadErrorHandler(key, existingFiles, true);
      } else {
        fileUploadSuccessHandler(key, fileKey, result, isSingleFileUpload)
      }
    } catch (e) {
      setErrors([{ key: 'generic', message: t('error.server') }]);
    } finally {
      //Replace file mode prevents the loadingFile from being set to false before the corresponding
      //replacement is complete
      if (!replaceFileMode) {
        setLoadingFile(false);
      }
    }
  }

  const selectUploadButtons = Object.keys(uploadProps).map(_ => {
    return <div key={_} className={`${styles.selectUploadButton} ${_ === selectedUpload ? styles.selectedUploadButton : ''}`} onClick={() => setSelectedUpload(_)}>{uploadProps[_].label}</div>
  })

  const dragDropUploads = useMemo(() => {
    return ['featuredImage', 'additionalImages', 'featuredFloorplan', 'additionalFloorplans', 'profile_pdf', 'pdf', 'autocad'].map(uploadKey =>
      <div className={`${uploadKey !== selectedUpload ? 'd-none' : 'w-100'}`} key={uploadKey}>
        <DragDropImageUpload
          numberFiles={uploadProps[uploadKey].numberFiles}
          saveFilesHandler={(files) => saveFilesHandler(files, uploadKey)}
          onRemove={(file, index, files, replaceFileMode) => removeFileHandler(files, index, uploadKey, replaceFileMode)}
          files={uploadedFiles[uploadKey]}
          thumbnailSlide={true}
          hideButtons={uploadProps[uploadKey].hideButtons}
          loading={loadingFile}
          removeImageButton={!loadingFile}
          fileTypes={uploadProps[uploadKey].fileTypes}
          nonImageFileType={uploadProps[uploadKey].nonImageFileType}
          buttonsLight={true}
          isReadOnly={isReadOnly} />
      </div>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUpload, uploadProps, featuredImage, additionalImages, featuredFloorplan, additionalFloorplans, lodPdf, profilePdf, autocad, loadingFile])

  const FileUploadsSection = () => (
    <section className="d-flex flex-column w-100">
      {/* Floorplan image */}
      <div className={`${styles.uploadFiles} w-100 mb-4`}>
        <div className={`d-flex flex-wrap mb-2 ${styles.selectUploadButtons}`}>
          {selectUploadButtons}
        </div>
        <div>
          {dragDropUploads}
        </div>
      </div>
      {/* Other general files */}
      <div className="w-100 mb-4">
        <UploadInteralFilesButton
          buttonLabel={(
            intl.formatMessage({ id: 'ui.uploadItem' }, { item: t('file.internal_files') })
          )}
          errorMessagePrefix={t('error.general')}
          modalTitle={t('ui.fileManager')}
          profileCompany={spaceProfile.company}
          profileFileBundle={internalFilesBundle}
          profileFileBundleKey={internalFilesBundleKey}
          profileGuid={spaceProfile.guid}
          profileProperty={spaceProfile.property}
          updateField={updateField}
        />
      </div>
    </section>
  );

  const generalInfo =
    <>
      <FileUploadsSection />
      <div className="d-flex flex-wrap w-100">
        <div className={`${styles.field} pr-5`}>
          <AdminInput id={'company'} label={t('term.company')} value={spaceProfile.displayCompany || spaceProfile.company} readOnly customstyle={'mt-3 mb-3'} />
        </div>
        <div className={`${styles.field} pr-5`}>
          <AdminInput
            id={'property'}
            label={t('term.property')}
            value={propertyDisplayName || spaceProfile.display_name || spaceProfile.property}
            onChange={e => updateField('display_name', e, spaceProfile)}
            onBlur={() => saveProfile()}
            readOnly
            customstyle={'mt-3 mb-3'} />
        </div>
        <div className={`${styles.field} pr-5`}>
          <AdminInput
            id={'address'}
            label={t('term.address')}
            value={dynamicAddress ?? t('error.notAvailable')}
            onChange={e => updateField('address1', e, spaceProfile)}
            onBlur={() => saveProfile()}
            readOnly
            customstyle={'mt-3 mb-3'} />
        </div>
        <div className={`${styles.field} pr-5`}>
          {isReadOnly || !currentUser.is_id360_admin ?
            <AdminInput id={'unit'} label={t('term.unit')} value={spaceProfile.address2} readOnly customstyle={'mt-3 mb-3'} />
            :
            <SpaceSelect
              selectedCompany={{ value: spaceProfile.company }}
              selectedProperty={{ value: spaceProfile.property }}
              selectedSpace={{ label: spaceProfile.address2, value: spaceProfile.address2 }}
              setSelectedSpace={onChangeSpace} />
          }
        </div>
        <div className={`${styles.field} pr-5`}>
          <AdminInput id={'city-state-zip'} label={t('BasicInfoForm.addressLabel')} value={intl.formatMessage({ id: 'BasicInfoForm.address' }, { city: spaceProfile.city || '', state: spaceProfile.state || '', postal: spaceProfile.postal || '' })} readOnly customstyle={'mt-3 mb-3'} />
        </div>
        <div className={`${styles.field} pr-5`}>
          {renderField[get(formSchema, `client_provided_sq_ft.type`, 'text')]('basic_info.client_provided_sq_ft', spaceProfile, formSchema, fieldSettings, updateField, saveProfile, isReadOnly, errors, setErrors)}
        </div>
        <div className={`${styles.field} pr-5`}>
          {sqFootageError ? <Alert className={styles.alert} key="errors" variant='warning'>{t('BasicInfoForm.sqftMismatch')}</Alert> : null}
        </div>
      </div>
    </>

  const bottomInfo = !spaceProfile.parent_guid &&
    <>
      <div className="d-flex flex-wrap w-100">
        <div className={`${styles.field} pr-5`}>
          <ThemeSelect spaceProfile={spaceProfile} updateField={updateField} saveProfile={saveProfile} isReadOnly={isReadOnly} propStyles={styles.select} />
        </div>
      </div>
    </>
  return <BuildForm
    layoutSchema={layoutSchema}
    formSchema={formSchema}
    form={spaceProfile}
    setForm={setSpaceProfile}
    updateField={updateField}
    saveForm={saveProfile}
    errors={errors}
    setErrors={setErrors}
    isReadOnly={isReadOnly}
    fieldSettings={fieldSettings}
    setIsFormDirty={setIsFormDirty}
    setOpenSection={setOpenSection}
    openSection={openSection}
    embeddedContent={[{ top: generalInfo, bottom: bottomInfo }]}
    insertValues={insertValues}
    spaceProfilePhotos={spaceProfilePhotos}
    setSpaceProfilePhotos={setSpaceProfilePhotos}
  />
}

export default BasicInfoForm;
